import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Search, Phone } from "lucide-react";
import { NAV_LINKS } from "./marketingData";
import { TrackConsignment } from "./TrackConsignment";
import { QuoteForm } from "./QuoteForm";

export function MarketingNav({ heroDark = false }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";
  // Use dark text when scrolled (light glass bg) OR when the page hero is light.
  const light = scrolled || !heroDark;
  const linkCls = light
    ? "text-[#1A1A18]/70 hover:text-[#C84B31]"
    : "text-white/90 hover:text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.35)]";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAnchor = (href) => {
    setOpen(false);
    if (onHome) {
      const el = document.querySelector(href);
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -92 });
        else el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/" + href);
    }
  };

  return (
    <header
      data-testid="marketing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#F9F8F6]/90 backdrop-blur-xl border-b border-[#1A1A18]/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[92px]">
        <Link to="/" data-testid="marketing-logo" className="flex items-center gap-3.5 group">
          <img src="/ito-logo.png" alt="ITO" className="w-16 h-16 sm:w-[72px] sm:h-[72px] object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className={`font-heading font-black tracking-tight text-xl ${light ? "text-[#1A1A18]" : "text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.35)]"}`}>
              INDIAN TRANSPORT
            </div>
            <div className="font-heading font-black tracking-tight text-[#C84B31] text-xl">
              ORGANISATION
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`group relative text-sm font-medium transition-colors ${linkCls}`}
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-[#C84B31] transition-all duration-300 group-hover:w-full" />
              </Link>
            ) : (
              <button
                key={l.href}
                onClick={() => goAnchor(l.href)}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                className={`group relative text-sm font-medium transition-colors ${linkCls}`}
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-[#C84B31] transition-all duration-300 group-hover:w-full" />
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setTrackOpen(true)}
            data-testid="nav-track-btn"
            className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${linkCls}`}
          >
            <Search className="w-4 h-4" /> Track
          </button>
          <button
            onClick={() => setQuoteOpen(true)}
            data-testid="nav-quote-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors focus:ring-2 focus:ring-[#C84B31] focus:outline-none"
          >
            Get a quote
            <ArrowRight className="w-4 h-4" />
          </button>
          <Link
            to="/app"
            data-testid="marketing-login-btn"
            className={`hidden sm:inline-flex items-center rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors ${
              light ? "border-[#1A1A18]/20 text-[#1A1A18] hover:bg-[#1A1A18]/5" : "border-white/40 text-white hover:bg-white/10"
            }`}
          >
            Login
          </Link>
          <button
            className={`lg:hidden p-1 ${light ? "text-[#1A1A18]" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="marketing-nav-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-[#1A1A18]/10 px-5 py-4 space-y-1" data-testid="mobile-nav-menu">
          {NAV_LINKS.map((l) =>
            l.to ? (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-link-${l.label.toLowerCase()}-mobile`}
                className="block w-full text-left py-3 text-[#1A1A18] font-medium border-b border-[#1A1A18]/5">
                {l.label}
              </Link>
            ) : (
              <button key={l.href} onClick={() => goAnchor(l.href)} data-testid={`nav-link-${l.label.toLowerCase()}-mobile`}
                className="block w-full text-left py-3 text-[#1A1A18] font-medium border-b border-[#1A1A18]/5">
                {l.label}
              </button>
            )
          )}
          <button onClick={() => { setOpen(false); setTrackOpen(true); }} data-testid="nav-track-mobile"
            className="flex items-center gap-2 w-full text-left py-3 text-[#1A1A18] font-medium border-b border-[#1A1A18]/5">
            <Search className="w-4 h-4 text-[#C84B31]" /> Track a consignment
          </button>
          <button onClick={() => { setOpen(false); setQuoteOpen(true); }} data-testid="nav-quote-mobile"
            className="flex items-center gap-2 w-full text-left py-3 text-[#C84B31] font-semibold border-b border-[#1A1A18]/5">
            <Phone className="w-4 h-4" /> Get a quote / callback
          </button>
          <Link to="/app" onClick={() => setOpen(false)}
            className="block w-full text-left py-3 text-[#1A1A18] font-medium">
            Login
          </Link>
        </div>
      )}

      <TrackConsignment open={trackOpen} onClose={() => setTrackOpen(false)} />
      <QuoteForm open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </header>
  );
}
